import { useEffect, useRef } from 'react';
import { inputService } from '../services/InputService';
import { useGameStore } from '../store/gameSlice';
import { openInterface } from '../services/InterfaceService';
import { pulseAction } from '../store/actionPulseSlice';
import { useFloorTransitionStore } from '../store/floorTransitionSlice';
import { useTransitionStore } from '../store/transitionSlice';
import { useCinematicStore } from '../store/cinematicSlice';
import { useReconnectStore } from '../store/reconnectSlice';
import { DungeonAction } from '../data/controls.config';

/**
 * Actions shown as a HUD icon or visual prompt — each press must trigger the
 * "bump" animation even if the action is on cooldown or blocked by game state.
 */
const PULSABLE_DUNGEON_ACTIONS: readonly DungeonAction[] = [
  'BASIC_ATTACK',
  'INTERACT',
  'SKILL_1',
  'SKILL_2',
  'SKILL_3',
  'SKILL_4',
  'INVENTORY',
  'OPEN_MAP',
  'PAUSE',
];

let dungeonInputCallback: (() => void) | null = null;
let hubInputCallback: (() => void) | null = null;

/**
 * Register (or unregister) the per-frame dungeon input processor.
 * Called by `useDungeonInput` on mount/unmount so the single rAF loop
 * can invoke it with guaranteed ordering (before endFrame).
 */
export function registerDungeonInput(cb: (() => void) | null): void {
  dungeonInputCallback = cb;
}

/**
 * Counterpart of `registerDungeonInput` for the hub. The master loop calls one
 * OR the other depending on `gameplay`.
 */
export function registerHubInput(cb: (() => void) | null): void {
  hubInputCallback = cb;
}

/**
 * Single rAF loop that drives ALL input processing, even while the Pixi
 * ticker is stopped (pause).
 *
 * Execution order each frame:
 *   1. Dungeon input (movement, skills, inventory shortcut…)
 *   2. Global pause toggle
 *   3. `inputService.endFrame()` — flushes "just pressed" sets
 *
 * Menu-level navigation (CANCEL, UNPAUSE, UP/DOWN…) is event-based
 * (`useMenuInput`) and does not depend on this loop.
 */
export function useGameInput() {
  const rafRef = useRef<number>(0);

  useEffect(() => {
    const loop = () => {
      const { gameplay } = useGameStore.getState();
      // `useTransitionStore` covers the scene fade (enterHub / endRun / abandon):
      // opening the escape menu mid-fade pauses the ticker right as setGameplay
      // swaps the scene, leaving isPaused stuck true → hard freeze.
      const transitioning =
        useFloorTransitionStore.getState().active ||
        useTransitionStore.getState().active;
      const cinematic = useCinematicStore.getState().active;
      // Reconnecting / waiting for the host: the ReconnectOverlay owns the screen
      // and only its "Quitter" button (DOM) is actionable — gate gameplay input
      // (incl. pause) so the player can't act on a stale, unsynced avatar nor
      // open the escape menu behind the overlay.
      const reconnecting = useReconnectStore.getState().reconnecting;

      // During a floor transition OR a cinematic, ignore all gameplay inputs
      // (movement, pulse, pause, hub/dungeon callbacks). The screen is either
      // hidden or showing a scripted sequence; menu-level navigation
      // (CONFIRM/CANCEL for the cinematic dialogue) is event-based and unaffected.
      if (!transitioning && !cinematic && !reconnecting) {
        // Pulse only in the dungeon phase and when no interface is open —
        // otherwise inputs drive interface navigation and shouldn't bump the
        // dungeon HUD in the background.
        if (gameplay === 'dungeon') {
          for (let i = 0; i < PULSABLE_DUNGEON_ACTIONS.length; i++) {
            const a = PULSABLE_DUNGEON_ACTIONS[i];
            if (inputService.isActionJustPressed('dungeon', a)) {
              pulseAction(a);
            }
          }
        }

        if (gameplay === 'dungeon' || gameplay === 'hub') {
          if (inputService.isActionJustPressed('dungeon', 'PAUSE')) {
            openInterface('escape-menu');
          }
        }

        if (gameplay === 'dungeon') {
          dungeonInputCallback?.();
        } else if (gameplay === 'hub') {
          hubInputCallback?.();
        }
      }

      inputService.endFrame();
      rafRef.current = requestAnimationFrame(loop);
    };

    rafRef.current = requestAnimationFrame(loop);
    return () => cancelAnimationFrame(rafRef.current);
  }, []);
}
